import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertCircle, ArrowLeft, CheckCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { AuthenticatedRedirect } from "../components/auth/AuthenticatedRedirect";
import { RivaledFullLogo } from "../components/logos/RivaledFullLogo";
import { Button, Input } from "../components/ui";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const { error: reqError } = await authClient.requestPasswordReset({
        email,
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (reqError) {
        setError(reqError.message ?? "Something went wrong");
      } else {
        setSent(true);
      }
    } catch {
      setError("Unable to reach the server. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-surface-950 overflow-hidden">
      <AuthenticatedRedirect />
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(38,217,181,0.06),transparent_60%)]" />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-full max-w-md mx-4"
      >
        <div className="flex justify-center mb-10">
          <Link to="/">
            <RivaledFullLogo className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
          </Link>
        </div>

        <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

          <div className="relative px-8 py-10">
            {sent ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="flex items-center justify-center size-12 rounded-full bg-brand-500/10 border border-brand-500/20">
                    <CheckCircle className="size-6 text-brand-400" />
                  </div>
                </div>
                <h1 className="text-2xl font-display font-bold tracking-tight text-white uppercase">
                  Check Your Email
                </h1>
                <p className="text-sm text-surface-400">
                  If an account exists for <span className="text-surface-200">{email}</span>, you'll
                  receive a password reset link shortly.
                </p>
                <Link
                  to="/sign-in"
                  className="inline-flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors mt-4"
                >
                  <ArrowLeft className="size-4" />
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-display font-bold tracking-tight text-white uppercase">
                    Reset Password
                  </h1>
                  <p className="text-sm text-surface-400 mt-2">
                    Enter your email and we'll send you a reset link
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="Email"
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                    placeholder="you@example.com"
                  />

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-error-500/10 border border-error-500/20 text-error-400 text-sm"
                    >
                      <AlertCircle className="size-4 shrink-0" />
                      {error}
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading}
                    className="w-full py-2.5"
                  >
                    {isLoading ? <Loader2 className="size-4 animate-spin" /> : "Send Reset Link"}
                  </Button>
                </form>

                <p className="text-center text-sm text-surface-400 mt-6">
                  <Link
                    to="/sign-in"
                    className="inline-flex items-center gap-1 text-brand-400 hover:text-brand-300 transition-colors"
                  >
                    <ArrowLeft className="size-3.5" />
                    Back to sign in
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
